import React from 'react';
import { Calendar, Clock, ArrowRight } from 'lucide-react';

const STATUS_COLORS = {
  'planned': 'bg-gray-500/20 border-gray-500/40 text-gray-400',
  'in-progress': 'bg-blue-500/20 border-blue-500/40 text-blue-400',
  'done': 'bg-green-500/20 border-green-500/40 text-green-400',
  'on-hold': 'bg-amber-500/20 border-amber-500/40 text-amber-400',
};

const PRIORITY_COLORS = {
  'critical': 'bg-destructive/20 text-destructive',
  'high': 'bg-amber-500/15 text-amber-500',
  'medium': 'bg-blue-500/15 text-blue-500',
  'low': 'bg-muted text-muted-foreground',
};

function TimelineView({ items, onItemClick, onItemUpdate }) {
  // Sort items by start_date if present, else by created_at
  const sortedItems = [...items].sort((a, b) => {
    const aDate = a.start_date || a.created_at || '';
    const bDate = b.start_date || b.created_at || '';
    return aDate.localeCompare(bDate);
  });

  // Group by quarter/month
  const groupByPeriod = (items) => {
    const groups = {};
    items.forEach(item => {
      const date = item.start_date || item.created_at;
      let period = 'Unscheduled';
      if (date) {
        const d = new Date(date);
        const q = Math.ceil((d.getMonth() + 1) / 3);
        period = `Q${q} ${d.getFullYear()}`;
      }
      if (!groups[period]) groups[period] = [];
      groups[period].push(item);
    });
    return groups;
  };

  const groups = groupByPeriod(sortedItems);
  const periods = Object.keys(groups);

  // Calculate Gantt bar width
  const getBarWidth = (item) => {
    if (!item.start_date || !item.end_date) return null;
    const start = new Date(item.start_date);
    const end = new Date(item.end_date);
    const days = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));
    return Math.min(100, Math.max(10, days * 2));
  };

  return (
    <div data-testid="timeline-view">
      {sortedItems.length === 0 ? (
        <div className="text-center py-16 bg-card border border-border rounded-xl">
          <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Items on Timeline</h3>
          <p className="text-sm text-muted-foreground">Add items with start/end dates to visualize them here</p>
        </div>
      ) : (
        <div className="space-y-8">
          {periods.map(period => (
            <div key={period}>
              <div className="flex items-center gap-3 mb-4">
                <h3 className="text-sm font-semibold text-primary uppercase tracking-wider">{period}</h3>
                <div className="flex-1 h-px bg-border" />
                <span className="text-xs text-muted-foreground">{groups[period].length} items</span>
              </div>

              <div className="space-y-2">
                {groups[period].map(item => {
                  const barWidth = getBarWidth(item);
                  return (
                    <div
                      key={item._id}
                      onClick={() => onItemClick(item)}
                      className="group bg-card border border-border rounded-xl p-4 cursor-pointer card-hover"
                      data-testid={`timeline-item-${item._id}`}
                    >
                      <div className="flex items-start gap-4">
                        {/* Left: timeline dot */}
                        <div className="flex flex-col items-center mt-1">
                          <div className={`w-3 h-3 rounded-full border-2 ${STATUS_COLORS[item.status]?.replace('bg-', 'bg-').replace('text-', 'border-') || 'border-muted-foreground'}`} />
                          <div className="w-0.5 flex-1 bg-border mt-1 min-h-[20px]" />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <h4 className="font-semibold text-sm group-hover:text-primary transition-colors">{item.title}</h4>
                              {item.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{item.description}</p>}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className={`text-xs px-2 py-0.5 rounded-md font-medium capitalize ${STATUS_COLORS[item.status] || ''}`}>{item.status}</span>
                              <span className={`text-xs px-2 py-0.5 rounded-md font-medium capitalize ${PRIORITY_COLORS[item.priority] || ''}`}>{item.priority}</span>
                            </div>
                          </div>

                          {/* Gantt bar */}
                          {barWidth && (
                            <div className="mt-3">
                              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                                <span>{new Date(item.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                                <ArrowRight className="h-3 w-3" />
                                <span>{new Date(item.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                              </div>
                              <div className="h-2.5 bg-muted rounded-full overflow-hidden">
                                <div className={`h-full rounded-full transition-all ${item.status === 'done' ? 'bg-green-500' : item.status === 'in-progress' ? 'bg-blue-500' : 'bg-primary/60'}`} style={{ width: `${barWidth}%` }} />
                              </div>
                            </div>
                          )}

                          {/* Meta */}
                          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                            <span className="badge-feature px-1.5 py-0.5 rounded text-xs font-medium capitalize">{item.type}</span>
                            {item.owner && <span>{item.owner.split('@')[0]}</span>}
                            {item.start_date && !barWidth && (
                              <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(item.start_date).toLocaleDateString()}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default TimelineView;
